"use client";

import { getLink } from "@utils/linkUtils";
import { useLocale } from "next-intl";
import { getImageProps } from "next/image";
import React, { FC, useMemo } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Image from "@/components/ui/Image/Image";
import useBreakPoint from "@/hooks/useBreakpoint";

import RichText from "../RichText/RichText";
import { TextBaseline as TextBaseLineType } from "../UnitGallery/type";

export interface TextBaselineProps {
  entry: TextBaseLineType;
}

export const TextBaseline: FC<TextBaselineProps> = ({ entry }) => {
  const { isMd } = useBreakPoint("md");
  const locale = useLocale();

  const gridSize = useMemo(() => {
    let sizeClass: string;
    switch (entry.gridFlexibility) {
      case "6/12 columns":
        sizeClass = "md:w-6/12";
        break;
      case "4/12 columns":
        sizeClass = "md:w-4/12";
        break;
      case "full page":
        sizeClass = "md:w-20\\/24";
        break;
    }
    return sizeClass;
  }, [entry.gridFlexibility]);

  const imageBackground = useMemo(() => {
    if (entry.background) {
      const backgroundDesktopImage = getImageProps({
        alt: "",
        width: 1600,
        height: 1024,
        quality: 80,
        src: entry.background.url,
      });
      const backgroundMobileImage = getImageProps({
        alt: "",
        width: 1024,
        height: 768,
        quality: 80,
        src: entry.background.url,
      });
      return {
        backgroundImage: `url(${isMd ? backgroundDesktopImage.props.src : backgroundMobileImage.props.src ? backgroundMobileImage.props.src : backgroundDesktopImage.props.src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      } as React.CSSProperties;
    } else {
      return undefined;
    }
  }, [isMd, entry.background, entry.backgroundMobile]);

  return (
    <div className="my-[20px]" style={imageBackground}>
      <div className={`mx-auto ${gridSize}`}>
        <div className={`flex flex-col ${entry.contentAlignment === "center" ? "items-center text-center" : "items-start"}`}>
          {entry.logo && (
            <Image
              className={`${entry.contentAlignment === "center" ? "mx-auto" : " "}`}
              src={entry.logo.url}
              alt={entry.logo.title}
              width={entry.logo.width || 100}
              height={entry.logo.height || 100}
              fit="pad"
            />
          )}
          {entry.title && (
            <h2
              style={{ color: entry?.titleColor }}
              className="font-normal uppercase font-primary text-l md:text-xl my-l accent-font accent-title accent-color"
            >
              {entry.title}
            </h2>
          )}
          {entry.description && <RichText content={entry?.description} descriptionColor={entry?.descriptionColor} />}
          {entry.cta && (
            <Button
              href={getLink(entry.cta, locale)}
              customClass="px-0 font-medium"
              type="button"
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.TERTIARY}
              theme={ButtonTheme.BLACK}
            >
              {entry.cta.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextBaseline;
